import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../context';
import { FilterValue } from '../../components';
import { LOCALES } from '../../constants';
import { LanguageByCode, optArrItem } from '../../utils';
import { useFilter } from './useFilter';

export enum CardProps {
  allowComments = 'allowComments',
  allowCardReactions = 'allowCardReactions',
  allowSharing = 'allowSharing',
  allowCardMentions = 'allowCardMentions',
  shouldUpdatePostedTimeByComments = 'shouldUpdatePostedTimeByComments',
  showAllAuthors = 'showAllAuthors',
}

interface UseCardFilterArgs {
  filter: FilterValue[];
  showEnableComments?: boolean;
  showDisplayUser?: boolean;
  showEnableMentions?: boolean;
}

export const useCardFilter = ({
  filter,
  showEnableComments = true,
  showDisplayUser = true,
  showEnableMentions = true,
}: UseCardFilterArgs) => {
  const { t } = useTranslation();

  const { getGroupValue } = useFilter();

  const { localeId } = useContext(UserContext).userInfo.userData;

  const localeValue = useMemo(
    () => getGroupValue({ filter, groupId: 'localeId' }) ?? localeId,
    [filter, getGroupValue, localeId]
  );

  const localeDropdown = useMemo(() => {
    return {
      options: LOCALES.map(({ id, code }) => {
        return { id, title: LanguageByCode[code as keyof typeof LanguageByCode] };
      }),
    };
  }, []);

  return {
    groups: [
      {
        id: 'card',
        multiCheck: true,
        items: [
          ...optArrItem(showEnableComments, {
            id: CardProps.allowComments,
            label: t('cardFilter.enable-comments'),
            value: CardProps.allowComments,
          }),
          {
            id: CardProps.allowCardReactions,
            label: t('cardFilter.enable-reactions'),
            value: CardProps.allowCardReactions,
          },
          {
            id: CardProps.allowSharing,
            label: t('cardFilter.enable-sharing'),
            value: CardProps.allowSharing,
          },
          {
            id: CardProps.shouldUpdatePostedTimeByComments,
            label: t('cardFilter.update-time-by-comments'),
            value: CardProps.shouldUpdatePostedTimeByComments,
          },
          ...optArrItem(showDisplayUser, {
            id: CardProps.showAllAuthors,
            label: t('cardFilter.display-user'),
            value: CardProps.showAllAuthors,
          }),
          ...optArrItem(showEnableMentions, {
            id: CardProps.allowCardMentions,
            label: t('cardFilter.enable-mentions'),
            value: CardProps.allowCardMentions,
          }),
        ],
      },
      {
        id: 'localeId',
        title: t('cardFilter.default-language'),
        value: localeValue,
        dropdown: localeDropdown,
      },
      {
        id: 'backgroundColor',
        value: getGroupValue({ filter, groupId: 'backgroundColor' }) ?? '#ffffff',
        colorPicker: { label: t('cardFilter.background-color') },
      },
    ],
  };
};
